import { faPlane } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlightDetails,
  ListOFCatalogPrice,
  Outbound,
} from 'ts/flight/interfaces/FlightDetailsResponse';
import { faFileLines, faMoneyBill1 } from '@fortawesome/free-regular-svg-icons';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import RulesModal from './RulesModal';
import PassengerList from './PassengersList';
import BoundsList from './BoundsList';
import { CmsPageResponse } from 'ts/shared/interfaces/CmsPageResponse';
import GeneralConditionsModal from 'components/shared/GeneralConditionsModal';
import HandleFee from 'components/shared/HandleFee';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import SalesPrice from 'components/shared/SalesPrice';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { FlProposal } from 'ts/shared/interfaces/CreateProposalParams';
import { useSearchParams } from 'react-router-dom';
import { MainContext } from 'contexts/MainContext';
import { isTT } from 'constants/generic';

interface BookSummaryProps {
  flight: FlightDetails;
  bounds: Outbound[];
  successPage?: boolean;
  couponCode: string;
  setCouponCode: (code: string) => void;
  onClickApply: () => void;
  addPrice: number;
  totalPrice?: number;
  totalCurrency?: string;
  bags?: ListOFCatalogPrice[];
  netravelPage?: CmsPageResponse;
}

const BookSummary: FC<BookSummaryProps> = ({ bounds, flight, successPage, ...props }) => {
  const [t] = useTranslation();
  const [showRules, setShowRules] = useState(0);
  const priceObj = flight.flighPrice.grossAndTaxWithMarkup;
  const total = priceObj.value + props.addPrice + parseInt(props.couponCode || '0');
  const { proposal, setProposal } = useContext(MainContext);
  const [urlParams] = useSearchParams();

  const onClickProposal = () => {
    if (!proposal || !setProposal) return;

    const bagsPrice = props.bags?.reduce((acc, bag) => acc + bag.price, 0) || 0;
    const existingProposalIndex = proposal.flights.findIndex(
      (proposal) => proposal.item.id === flight.id
    );

    if (existingProposalIndex !== -1) {
      const updatedFlights = proposal.flights.filter((_, index) => index !== existingProposalIndex);
      setProposal({ ...proposal, flights: updatedFlights });
    } else {
      const newProposal: FlProposal = {
        item: JSON.parse(JSON.stringify(flight)),
        currency: flight.flighPrice.grossAndTaxWithMarkup.currency,
        price: flight.flighPrice.grossAndTaxWithMarkup.value + bagsPrice,
        displayPrice: true,
        searchToken: urlParams.get('searchToken') || '',
        bags: props.bags,
      };

      setProposal({ ...proposal, flights: [...proposal.flights, newProposal] });
    }
  };

  const totalPrice = formatPriceCurrency(
    successPage ? props.totalPrice || 0 : total,
    getCurrency(successPage ? props.totalCurrency || '' : priceObj.currency)
  );

  return (
    <div>
      <div
        className={
          'details-summary book-summary default-shadow flight-details-summary position-relative' +
          (successPage ? ' flight-details-summary-success' : '')
        }
      >
        <div className="title-underline title-header-grey text-lg mb-20">
          {t('passenger.order_details')}
        </div>
        <ProposalHeart
          active={!!proposal?.flights.some((f) => f.item.id === flight.id)}
          onClickPropsal={onClickProposal}
        />

        <div className="title text-st text-contrast">
          <FontAwesomeIcon icon={faPlane as any} className="mr-5" size="sm" />
          {t('flight.details')}{' '}
          {!isTT && flight.flightSource?.includes('ALP') && (
            <span className="text-light">({flight.provider})</span>
          )}
        </div>

        <BoundsList bounds={bounds} />

        {!!props.netravelPage?.page?.languages?.[0]?.title_3?.body && (
          <div className="text-lh mb-20">
            <div className="divider mb-20"></div>
            <div className="text-contrast text-bold">
              {props.netravelPage.page.languages[0].title_3.desc}
            </div>
            <div
              className="text-st"
              dangerouslySetInnerHTML={{
                __html: props.netravelPage.page.languages[0].title_3.body,
              }}
            />
          </div>
        )}

        {!successPage && (
          <Fragment>
            <div className="divider mb-20"></div>
            <div className="title text-st text-contrast">
              <FontAwesomeIcon icon={faFileLines as any} className="mr-5" size="sm" />
              {t('flight.info_and_conditions')}
            </div>
            <div className="text-sm mb-20 text-lh-sm">
              <span>{t('flight.it_is_your')}</span>

              <span
                onClick={() => setShowRules(flight.flightSource === 'ALP24F' ? 2 : 1)}
                className="text-contrast text-underline"
              >
                {t('flight.all_rules')}
              </span>
              <span>{t('flight.according_this')}</span>
            </div>
            <HandleFee name="Flights" />
          </Fragment>
        )}

        <div className="divider mb-20"></div>
        <div className="flex-between mb-10">
          <div className="title text-st text-contrast">
            <FontAwesomeIcon icon={faMoneyBill1 as any} className="mr-5" size="sm" />
            {t('flight.booking_summary')}
          </div>
          <span className="text-bold text-md">{totalPrice}</span>
        </div>

        {!successPage && <PassengerList flight={flight} />}

        <div className="mt-20 flex-between text-lg text-bold title-footer-grey">
          <span>{t('flight.total_per_package')}</span>
          <span className="text-contrast">{totalPrice}</span>
        </div>

        {!successPage && flight.provider === 'LOW' && (
          <div className="fl-pair-ticket-ico mt-15 text-bold text-sm text-center bg-danger p-510">
            All Low-cost bookings are Non-refundable!!!
          </div>
        )}

        {showRules === 1 && (
          <RulesModal
            flightId={flight.id}
            onClose={() => setShowRules(0)}
            tokenKey={'searchToken'}
          />
        )}
        {showRules === 2 && (
          <GeneralConditionsModal
            product="Flights"
            name="Charter Flights"
            onClose={() => setShowRules(0)}
          />
        )}
      </div>
      {!successPage && (
        
          <SalesPrice
            currency={priceObj.currency}
            setSaleValue={props.setCouponCode}
            saleValue={props.couponCode}
            price={priceObj.value + props.addPrice}
            onClickApply={props.onClickApply}
          />
        
      )}
    </div>
  );
};

export default withErrorBoundary(BookSummary, 'BookSummary');
